import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";

interface FileDropZoneProps {
  disabled?: boolean;
  onFile: (file: File) => void;
}

/**
 * Drag-and-drop upload strip: drop a file anywhere on it, or click to browse.
 * The API validates type and size — this only hands the file over.
 */
const FileDropZone: React.FC<FileDropZoneProps> = ({ disabled, onFile }) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onFile(file);
    }
  };

  const handleBrowse = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-picking the same file fires the change event again.
    event.target.value = "";
    if (file) {
      onFile(file);
    }
  };

  return (
    <button
      type="button"
      className={`mb-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-6 text-sm transition-colors ${
        dragOver
          ? "border-primary-40 bg-primary-95 text-primary-40 dark:border-primary-90 dark:bg-primary-20 dark:text-primary-90"
          : "border-grey-90 text-grey-40 dark:border-grey-50 dark:text-grey-80"
      }`}
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(event) => {
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      disabled={disabled}
    >
      <Upload size={18} strokeWidth={1.5} />
      {t("materials.dropZoneHint", "Drag a file here, or click to browse")}
      <input type="file" className="hidden" ref={fileInputRef} onChange={handleBrowse} />
    </button>
  );
};

export default FileDropZone;
